import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { SchoolSwitcher } from "./SchoolSwitcher";
import {
  EyeIcon,
  ChevronDownIcon,
  XMarkIcon,
  HomeIcon,
  CalendarIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  UserGroupIcon,
  UsersIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  PencilSquareIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@gradebook/ui";
import { useAuthStore, type UserRole } from "@/store/authStore";
import { holdsAnyRole } from "@/lib/roles";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { useSchoolRoutes } from "@/lib/routes";
import { useGradebookEnabled } from "@/hooks/useSchoolSettings";
import { ImpersonationUserPicker } from "@/components/auth/ImpersonationUserPicker";
import { BuildInfo } from "@/components/layout/BuildInfo";
import { ReportProblemButton } from "@/components/layout/ReportProblemButton";

const impersonationRoles: { value: UserRole; label: string }[] = [
  { value: "teacher", label: "Teacher" },
  { value: "student", label: "Student" },
  { value: "parent", label: "Parent" },
];

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

interface SidebarContentProps {
  navSections: NavSection[];
  isActive: (path: string) => boolean;
  /** Closes the mobile drawer once a link is followed; absent on desktop. */
  onLinkClick?: () => void;
  /**
   * The rail sits on the tinted canvas, so the current page is marked by giving
   * it the panel's own white; inside the drawer, which is already white, that
   * would be invisible and a tint is used instead.
   */
  onCanvas: boolean;
  /** Whether to offer the "View as" impersonation menu at all. */
  canImpersonate: boolean;
  /** An impersonation request is in flight, so the menu is disabled. */
  impersonating: boolean;
  onRoleSelect: (role: UserRole) => void;
}

/**
 * The nav itself, shared by the mobile drawer and the desktop rail.
 *
 * Declared at module scope deliberately. While this lived inside `AppSidebar`,
 * every render of the parent produced a new component *type*, so React
 * unmounted and remounted this whole subtree instead of updating it. That threw
 * away the DOM nodes — nav links lost the hover the pointer was still inside,
 * which read as a flash — along with every piece of state below them, including
 * whether the "View as" menu was open. The remount also fed itself: mounting
 * the menu scheduled another parent render, which remounted again, some 260
 * times a second.
 */
function SidebarContent({
  navSections,
  isActive,
  onLinkClick,
  onCanvas,
  canImpersonate,
  impersonating,
  onRoleSelect,
}: SidebarContentProps) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <nav className="min-h-0 flex-1 overflow-y-auto">
        {navSections.map((section, sectionIdx) => (
          <div key={sectionIdx}>
            {section.title && (
              <h3 className="px-[11px] pb-1 pt-2.5 text-[10px] font-semibold uppercase leading-none tracking-[0.07em] text-neutral-500">
                {section.title}
              </h3>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const current = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onLinkClick}
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "my-0.5 flex items-center gap-[9px] rounded-[10px] px-[11px] py-[7px] text-[12.5px] transition-colors",
                    current
                      ? onCanvas
                        ? "bg-card font-semibold text-primary-800 shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                        : "bg-primary-50 font-semibold text-primary-800"
                      : cn(
                          "text-neutral-700",
                          onCanvas ? "hover:bg-card/60" : "hover:bg-neutral-100"
                        )
                  )}
                >
                  <Icon className="size-4 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="flex-none pt-2">
        {/* Impersonation - only for admins when not already impersonating */}
        {canImpersonate && (
          <Menu as="div" className="relative">
            <Menu.Button
              disabled={impersonating}
              className={cn(
                "flex w-full items-center gap-1.5 rounded-[10px] px-[11px] py-2.5 text-[11.5px] text-muted-foreground transition-colors disabled:opacity-50",
                onCanvas ? "hover:bg-card/60" : "hover:bg-neutral-100"
              )}
            >
              <EyeIcon className="size-[22px] rounded-full bg-neutral-200 p-1 text-neutral-600" />
              View as
              <ChevronDownIcon className="size-3.5" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute bottom-full left-0 right-0 z-10 mb-1 overflow-hidden rounded-[14px] border bg-card py-1 shadow-lg focus:outline-none">
                <div className="px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
                  Impersonate Role
                </div>
                {impersonationRoles.map(({ value, label }) => (
                  <Menu.Item key={value}>
                    {/* `focus`, not the deprecated `active`, is Headless UI v2's
                        name for "this item is the keyboard/pointer target". */}
                    {({ focus }) => (
                      <Button
                        variant="ghost"
                        onClick={() => onRoleSelect(value)}
                        className={cn(
                          focus ? "bg-muted" : "",
                          "h-auto w-full justify-start rounded-none px-4 py-2 text-left text-sm font-normal text-neutral-700"
                        )}
                      >
                        {label}
                      </Button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        )}

        {/* Only rendered for someone who belongs to more than one school. */}
        <SchoolSwitcher />

        {/* For what goes wrong without throwing: a wrong total, a missing
            student, a button that does nothing. Renders nothing unless Sentry
            is configured. */}
        <ReportProblemButton />

        {/* Which bundle is running: commit sha + age of the deploy */}
        <BuildInfo />
      </div>
    </div>
  );
}

interface AppSidebarProps {
  /** Whether the mobile drawer is showing; the rail itself is always mounted. */
  navOpen?: boolean;
  onNavClose?: () => void;
}

export function AppSidebar({ navOpen = false, onNavClose = () => {} }: AppSidebarProps) {
  const { user, impersonation, startImpersonation, getEffectiveRoles } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useSchoolRoutes();
  const [impersonating, setImpersonating] = useState(false);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);

  // The whole set, not the primary one. `ProtectedRoute` admits on any office
  // held, so gating the nav on precedence hid entries the router would happily
  // serve: a teacher granted `coordinator` outranks `teacher` and lost the
  // gradebook, and the teacher-who-is-also-a-parent this model exists for never
  // saw "My Children".
  const effectiveRoles = getEffectiveRoles();
  const holds = (...roles: UserRole[]) => holdsAnyRole(effectiveRoles, roles);
  const isAdmin = user?.roles?.includes("admin") ?? false;
  const isImpersonating = impersonation?.isImpersonating ?? false;
  // Whether the school offers grades at all. False strips every entry below that
  // leads to one — the calendar and the course catalogue included — for every
  // role, and leaves the register, the roster and the rest of the admin section:
  // what a school live on attendance alone actually uses. Defaults to on, so
  // nothing changes for a school that has not touched it; see
  // `useGradebookEnabled`.
  const gradebookEnabled = useGradebookEnabled();

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setUserPickerOpen(true);
  };

  const handleUserSelect = async (user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  }) => {
    if (!selectedRole) return;

    setImpersonating(true);
    try {
      const response = await api.post("/api/auth/impersonate", {
        role: selectedRole,
        personId: user.id,
      });
      startImpersonation(
        response.data.token,
        response.data.impersonatingRoles,
        response.data.impersonatedUser
      );
      setUserPickerOpen(false);
      setSelectedRole(null);
      navigate(routes.dashboard());
    } catch (error) {
      console.error("Failed to impersonate:", error);
    } finally {
      setImpersonating(false);
    }
  };

  const handleUserPickerClose = () => {
    setUserPickerOpen(false);
    setSelectedRole(null);
  };

  // Build navigation sections based on role
  const getNavSections = (): NavSection[] => {
    const sections: NavSection[] = [];

    // Main navigation - always visible
    const mainItems: NavItem[] = [
      { name: "Dashboard", href: routes.dashboard(), icon: HomeIcon },
    ];

    // The calendar lists assignment due dates and nothing else, so with the
    // module off it is an empty page rather than a thinner one. The roles are
    // the route's own: a parent and a coordinator were being offered a link
    // that `ProtectedRoute` then bounced back to the dashboard.
    if (gradebookEnabled && holds("teacher", "admin", "student")) {
      mainItems.push({ name: "Calendar", href: routes.calendar(), icon: CalendarIcon });
    }

    if (holds("student") && gradebookEnabled) {
      mainItems.push({ name: "My Grades", href: routes.myGrades(), icon: AcademicCapIcon });
      mainItems.push({ name: "Year-End Grades", href: routes.myYearEndGrades(), icon: ClipboardDocumentCheckIcon });
    }

    if (holds("parent")) {
      // The children themselves are not a gradebook feature - a parent with the
      // gradebook off still has a contact record and an attendance history to
      // look at - so only the grades entry goes.
      mainItems.push({ name: "My Children", href: routes.parent(), icon: UserGroupIcon });
      if (gradebookEnabled) {
        mainItems.push({ name: "Year-End Grades", href: routes.myYearEndGrades(), icon: ClipboardDocumentCheckIcon });
      }
    }

    if (holds("teacher", "admin")) {
      if (gradebookEnabled) {
        mainItems.push({ name: "Gradebook", href: routes.gradebook(), icon: BookOpenIcon });
        // The catalogue goes with it: sections, terms and enrolments are the
        // scaffolding a gradebook hangs off, and a school running attendance
        // alone takes its register by homeroom, not by course.
        mainItems.push({ name: "Courses", href: routes.courses(), icon: AcademicCapIcon });
      }
      // Import stays: it brings in students and staff as well as grades.
      mainItems.push({ name: "Import", href: routes.bulkImport(), icon: ArrowUpTrayIcon });
    }

    // Beside the importer rather than inside it, and admin-only: an enrolment
    // packet is a named child's medical record, which is a different thing from
    // a roster spreadsheet and is refused to a teacher by the API too.
    if (isAdmin && !isImpersonating) {
      mainItems.push({
        name: "Enrolment forms",
        href: routes.studentIntake(),
        icon: DocumentTextIcon,
      });
    }

    if (holds("teacher") && gradebookEnabled) {
      mainItems.push({ name: "My Submissions", href: routes.mySubmissions(), icon: DocumentTextIcon });
      mainItems.push({ name: "My Grade Changes", href: routes.myGradeChanges(), icon: PencilSquareIcon });
    }

    sections.push({ title: "Main", items: mainItems });

    // Attendance gets a section of its own rather than one more entry under
    // Main: it is scoped to a homeroom and a day where everything above is
    // scoped to a course and a term, and the design (1a) grows more entries
    // here as the rest of the module lands.
    if (holds("teacher", "admin")) {
      const attendanceItems: NavItem[] = [];

      // The office's day (1b) leads the section, and `/attendance` itself lands
      // here: "who is not in, and who has been collected" across the school is
      // the question the office opens attendance to answer, where the register
      // answers it for one room. Admin-only, and the one place attendance
      // narrows rather than widens — a teacher's register is their own room,
      // and this is every child in the school with the name of whoever
      // collected them.
      if (isAdmin && !isImpersonating) {
        attendanceItems.push({
          name: "Overview",
          href: routes.attendanceOverview(),
          icon: ClipboardDocumentListIcon,
        });
      }

      attendanceItems.push({
        name: "Today",
        href: routes.attendanceToday(),
        icon: ClipboardDocumentCheckIcon,
      });

      // Totals over a term. Everyone in the section gets it: the office reports
      // on the whole school, a teacher on the homerooms they take the register
      // for, and the page offers each only what the API will answer.
      attendanceItems.push({
        name: "Reports",
        href: routes.attendanceReports(),
        icon: ChartBarIcon,
      });

      // The groups the register is taken for, so they belong to this section
      // rather than to Admin. Only an admin sees the entry: reading which
      // groups exist is what every teacher's register picker needs, but
      // creating, renaming and dissolving them is school configuration, and
      // the route enforces the same thing.
      if (isAdmin && !isImpersonating) {
        attendanceItems.push({
          name: "Homerooms",
          href: routes.attendanceHomerooms(),
          icon: UserGroupIcon,
        });
      }

      sections.push({ title: "Attendance", items: attendanceItems });
    }

    // People section for teachers/admins
    if (holds("teacher", "admin")) {
      const peopleItems: NavItem[] = [
        { name: "Students", href: routes.students(), icon: UserGroupIcon },
      ];

      if (isAdmin && !isImpersonating) {
        // First in the section, because it is the superset: Teachers and Parents
        // are the same rows seen through one office each, and "who is here at
        // all" is the question the other two could not answer between them.
        peopleItems.unshift({ name: "People", href: routes.adminPeople(), icon: UsersIcon });
        peopleItems.push({ name: "Teachers", href: routes.adminTeachers(), icon: UserGroupIcon });
        peopleItems.push({ name: "Parents", href: routes.adminParents(), icon: UserGroupIcon });
      }

      sections.push({ title: "People", items: peopleItems });
    }

    // Admin section
    if (isAdmin && !isImpersonating) {
      const adminItems: NavItem[] = [
        { name: "Academic Years", href: routes.adminAcademicYears(), icon: CalendarDaysIcon },
        { name: "Departments", href: routes.adminDepartments(), icon: BuildingOffice2Icon },
      ];

      // Approvals is the term-submission queue, so it goes with the gradebook -
      // there is nothing to approve while no grades are being submitted. Audit
      // Log and Settings stay: Settings is where the switch itself lives, and
      // locking an admin out of it would make the module impossible to turn
      // back on.
      if (gradebookEnabled) {
        adminItems.push({ name: "Approvals", href: routes.adminApprovals(), icon: CheckBadgeIcon });
        adminItems.push({ name: "Grade Changes", href: routes.adminGradeChanges(), icon: PencilSquareIcon });
        adminItems.push({ name: "Grade Posting", href: routes.adminGradePosting(), icon: ArrowUpTrayIcon });
      }

      adminItems.push({ name: "Audit Log", href: routes.adminAuditLog(), icon: ClipboardDocumentListIcon });
      adminItems.push({ name: "Settings", href: routes.settings(), icon: Cog6ToothIcon });

      sections.push({ title: "Admin", items: adminItems });
    }

    return sections;
  };

  const navSections = getNavSections();

  /**
   * The one entry the current page belongs to: the deepest whose href the path
   * is at or inside.
   *
   * Marking every entry the path merely starts with lit two pills at once —
   * `/import/enrolment-forms` filled in both "Import" and "Enrolment forms" —
   * and a page is in one place. Comparing whole segments matters as much as
   * taking the longest: `/students-archive` is not inside `/students`.
   * A page with no entry of its own still marks the entry it sits under, which
   * is why this is the longest *prefix* rather than an exact match.
   */
  const currentHref = navSections
    .flatMap((section) => section.items)
    .map((item) => item.href)
    .filter(
      (href) =>
        location.pathname === href || location.pathname.startsWith(href + "/")
    )
    .reduce<string | null>(
      (deepest, href) => (deepest !== null && deepest.length >= href.length ? deepest : href),
      null
    );

  const isActive = (path: string) => path === currentHref;

  // Everything `SidebarContent` needs, shared by the drawer and the rail.
  const sidebarProps = {
    navSections,
    isActive,
    canImpersonate: isAdmin && !isImpersonating,
    impersonating,
    onRoleSelect: handleRoleSelect,
  };

  return (
    <>
      {/* Mobile drawer. The trigger lives on the topbar. */}
      <Transition show={navOpen} as={Fragment}>
        <div className="fixed inset-0 z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-900/50" onClick={onNavClose} />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="fixed inset-y-0 left-0 flex w-72 flex-col rounded-r-[20px] bg-white p-3 shadow-xl">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute right-3 top-3 rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
                onClick={onNavClose}
              >
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="size-6" />
              </Button>
              <SidebarContent {...sidebarProps} onCanvas={false} onLinkClick={onNavClose} />
            </div>
          </Transition.Child>
        </div>
      </Transition>

      {/* Desktop rail: pills straight on the canvas, no panel of its own. */}
      <div
        data-testid="app-rail"
        className="hidden w-[190px] flex-none flex-col pr-3 lg:flex"
      >
        <SidebarContent {...sidebarProps} onCanvas />
      </div>

      {/* User picker modal for impersonation */}
      {selectedRole && (
        <ImpersonationUserPicker
          isOpen={userPickerOpen}
          onClose={handleUserPickerClose}
          role={selectedRole}
          onSelectUser={handleUserSelect}
        />
      )}
    </>
  );
}
